import { useFormContext } from "react-hook-form"
import { useEffect, useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    required,
    FileInput,
    SelectInput,
    FileField,
    useDataProvider,
    useRedirect,
    Toolbar,
    Button,
    SaveButton,
    TopToolbar,
    useNotify
} from 'react-admin';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import BackButton from "../../Components/BackButton";

const validateVersion = (value: any) => {
    if (!value) return '必填项';

    // 验证 x.y.z 格式（x,y,z 必须是数字）
    if (!/^\d+\.\d+\.\d+$/.test(value)) {
        return '版本号格式必须为 x.y.z（例如: 1.0.0）';
    }
    return undefined;
};

const ActionsToolbar = () => (
    <TopToolbar sx={{ justifyContent: 'start' }}>
        <BackButton />
    </TopToolbar>
);

// 自定义文件输入组件（确保在表单上下文中）
const CustomFileInput = (props: any) => {
    const { deviceTypes } = props;
    const form = useFormContext(); // 在表单上下文中安全使用

    const parseFileName = (fileName: string) => {
        // 移除文件扩展名
        let baseName = fileName.replace(/\.[^/.]+$/, "");

        const match = baseName.split('-');
        const lastSegment = match.pop() || ''; // 安全处理空值
        const cleanedLastSegment = lastSegment.split('.')[0]; // 取第一个点之前的部分
        match.push(cleanedLastSegment);

        const deviceType = match[0] || '';
        const otaTypeCode = match[1] || ''; // sw 或 fw
        const version = match[2] || '';
        baseName = match.join('-');


        // 根据 otaTypeCode 确定 OTA 类型
        const otaType = otaTypeCode === 'sw' ? 'SOFTWARE' : 'FIRMWARE';

        return {
            title: baseName, // 整个文件名（不含扩展名）作为标题
            deviceType,
            version,
            otaType,
        };
    };

    const handleFileSelect = (fileInfo: any) => {
        if (!fileInfo) {
            form.setValue('name', '');
            form.setValue('title', '');
            form.setValue('version', '');
            form.setValue('otaType', '');
            form.setValue('deviceType', '');
        }
        const parsedData = parseFileName(fileInfo.name);
        form.setValue('title', parsedData.title);
        form.setValue('version', parsedData.version);
        form.setValue('otaType', parsedData.otaType);
        // 如果解析到设备类型且存在于选项中
        if (parsedData.deviceType && deviceTypes.some((t: any) => t.name === parsedData.deviceType)) {
            form.setValue('deviceType', parsedData.deviceType);
        }
    };

    return (
        <FileInput {...props} onChange={handleFileSelect}>
            <FileField source="src" title="title" />
        </FileInput>
    );
};


// 自定义工具栏，添加确认弹窗
const CustomToolbar = (props: any) => {
    const [showDialog, setShowDialog] = useState(false);
    const form = useFormContext();
    const notify = useNotify(); // 添加通知钩子
    const redirect = useRedirect(); // 添加重定向钩子

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault(); // 阻止默认保存行为
        e.stopPropagation(); // 阻止事件冒泡
        // 先触发表单验证
        form.trigger().then((isValid) => {
            if (isValid) {
                setShowDialog(true);
            }
        });
    };

    const handleConfirm = () => {
        setShowDialog(false);
        notify('系统包创建成功', { type: 'success' });
        redirect('list', 'upgradeSystem'); // 跳转到列表页面
    };

    const handleClose = () => {
        setShowDialog(false);
    };

    const formValues = form.getValues();

    // 获取设备类型名称
    const getDeviceTypeName = (id: string) => {
        const type = props.deviceTypes.find((t: any) => t.id === id);
        return type ? type.name : id;
    };

    // 获取设备配置名称
    const getDeviceProfileName = (id: string) => {
        const profile = props.deviceProfile.find((p: any) => p.id === id);
        return profile ? profile.name : id;
    };

    // 获取OTA类型名称
    const getOtaTypeName = (type: string) => {
        return type === 'FIRMWARE' ? '固件' : '软件';
    };

    return (
        <>
            <Toolbar {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <SaveButton
                    label="保存"
                    onClick={handleClick}
                />
            </Toolbar>

            <Dialog open={showDialog} onClose={handleClose}>
                <DialogTitle>确认提交</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        请确认以下升级系统信息：
                    </DialogContentText>

                    <div style={{ marginTop: 20 }}>
                        <p><strong>标题:</strong> {formValues.title || '未填写'}</p>
                        <p><strong>设备类型:</strong> {formValues.deviceType ? getDeviceTypeName(formValues.deviceType) : '未选择'}</p>
                        <p><strong>版本:</strong> {formValues.version || '未填写'}</p>
                        <p><strong>设备配置:</strong> {formValues.deviceProfileId ? getDeviceProfileName(formValues.deviceProfileId) : '未选择'}</p>
                        <p><strong>release note:</strong> {formValues.description || '未填写'}</p>
                        <p><strong>OTA类型:</strong> {formValues.otaType ? getOtaTypeName(formValues.otaType) : '未选择'}</p>
                        <p><strong>固件文件:</strong> {formValues.file?.title || '未选择'}</p>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button label="取消" onClick={handleClose} />
                    <SaveButton label="确认" mutationOptions={{
                        onSuccess: handleConfirm
                    }}
                        type="button" />
                </DialogActions>
            </Dialog>
        </>
    );
};


export const upgradeSystemCreate = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [deviceProfile, setDeviceProfile] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 异步获取客户类型数据
        const fetchDeviceTypes = async () => {
            try {
                setLoading(true);
                const [deviceListResponse, deviceProfileResponse] = await Promise.all([
                    dataProvider.getDeviceList(),
                    dataProvider.getDeviceProfile({ pageNum: 1, pageSize: 100 })
                ]);

                const deviceTypesData = Array.isArray(deviceListResponse)
                    ? deviceListResponse
                    : deviceListResponse?.data || [];

                const types = deviceTypesData.map((item: any) => ({
                    id: item.name,
                    name: item.name
                }));

                // 处理设备配置数据 - 确保是数组
                const deviceProfilesData = Array.isArray(deviceProfileResponse)
                    ? deviceProfileResponse
                    : deviceProfileResponse?.data || [];

                const profiles = deviceProfilesData.map((item: any) => ({
                    id: item.id,
                    name: item.name
                }));

                setDeviceTypes(types);
                setDeviceProfile(profiles); // 修复：使用正确的状态设置函数
            } catch (error) {
                console.error("获取客户类型失败:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDeviceTypes();
    }, []);  // 空依赖数组确保只调用一次


    // 修复：正确转换数据为FormData格式
    const transformData = (data: any) => {
        const formData = new FormData();

        // 添加所有文本字段
        formData.append('title', data.title);
        formData.append('deviceType', data.deviceType);
        formData.append('version', data.version);
        formData.append('deviceProfileId', data.deviceProfileId);
        formData.append('url', data.url);
        formData.append('description', data.description);
        formData.append('otaType', data.otaType);

        // 添加文件（如果存在）
        if (data.file && data.file.rawFile) {
            formData.append('file', data.file.rawFile);
        }

        return formData;
    };

    return (
        <Create
            redirect="list"
            actions={<ActionsToolbar />}
            transform={transformData}
        >
            <SimpleForm sx={{ width: { lg: '40%', xs: '100%' } }}
                toolbar={<CustomToolbar deviceTypes={deviceTypes} deviceProfile={deviceProfile} />}
            >
                <TextInput
                    label="标题"
                    source="title"
                    validate={[required()]}
                />
                <SelectInput
                    label="设备类型"
                    source="deviceType"
                    choices={deviceTypes}
                    disabled={loading}
                    validate={[required()]}
                />
                <TextInput
                    label="版本"
                    source="version"
                    validate={[required(), validateVersion]}
                    placeholder="例如: 1.0.0"  // 添加提示
                />

                <SelectInput
                    label="设备配置"
                    source="deviceProfileId"
                    choices={deviceProfile}
                    disabled={loading}
                    helperText="设备配置:从thingsboard获取"
                    validate={[required()]}
                />

                <TextInput
                    label="release note"
                    source="description"
                    validate={[required()]}
                    multiline
                    rows={4}
                />

                <SelectInput
                    source="otaType"
                    label="OTA类型"
                    choices={[
                        {
                            id: 'FIRMWARE',
                            name: '固件',
                        },
                        {
                            id: 'SOFTWARE',
                            name: '软件',
                        }
                    ]}
                    validate={[required()]}
                />
                <CustomFileInput
                    source="file"
                    label="固件文件"
                    // accept={[".tar.gz", ".bin", ".img"]}
                    accept={{
                        'application/gzip': ['.tar.gz'],
                        'application/octet-stream': ['.bin', '.img']
                    }}
                    validate={[required()]}
                    deviceTypes={deviceTypes}
                />
            </SimpleForm>
        </Create>
    )
};

